import { Component, Input, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { takeUntil, finalize } from 'rxjs/operators';
import {
	RestService,
	IS_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	InfractionDetails,
	IS_EMPTY_LIST,
	IS_NOT_EMPTY_LIST,
} from 'cargo-chief-commons';

import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UtilService } from '@service/util/util.service';
import { BaseComponent } from '@shared/base/base.component';
import { SharedService } from '@service/shared/shared.service';
import {
	COMBINE_ALL_INFRACTIONS,
	GET_CC_OVERALL_CLASS,
	GET_CC_OVERALL_DISPLAY,
	GET_QUALITY_CLASS,
	GET_QUALITY_DISPLAY,
	TOOL_TIP_MESSAGE,
} from '@shared/utils/quality-data-utils';

@Component({
	selector: 'app-quality',
	templateUrl: './quality.component.html',
	styleUrls: ['./quality.component.scss'],
})
export class QualityComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() carrierId!: string;
	@Input() mc!: string;
	@Input() dot!: string;
	@Input() destinationCity!: string;
	@Input() destinationState!: string;
	@Input() originCity!: string;
	@Input() originState!: string;
	@Input() isCarrierInNetwork: boolean;
	@Input() ccBookingRateDisplay: string;
	@Input() ccEngagementRateDisplay: string;
	@Input() ccFalloffRateDisplay: string;
	@Input() ccTotalBookedLoads: number;
	@Input() ccScore: number;
	@Input() vettingOverallDisplay: string;
	@Input() vettingAuthorityDisplay: string;
	@Input() vettingInsuranceDisplay: string;
	@Input() vettingOperationsDisplay: string;
	@Input() vettingSafetyDisplay: string;
	@Input() vettingOtherDisplay: string;
	@Input() infractionDetails: InfractionDetails = {} as InfractionDetails;
	@Input() lastVettingTime: Date;
	@Input() carrierActive: boolean;
	@Input() forBrokerQualityData: boolean = false;
	@Input() forCarrierAccount: boolean = false;

	isLoadingQualityScoreDetails = false;

	ccScoreDisplay: string;
	isLoadingCarriersLoadsList: boolean = false;
	isLoadingCarrierQualityFromMcp: boolean = false;

	allInfractions: string[] = [];
	isAccountTypeAdmin: boolean = false;
	isMCPEnabled: boolean = false;

	IS_EMPTY_LIST = IS_EMPTY_LIST;
	IS_NOT_EMPTY_LIST = IS_NOT_EMPTY_LIST;
	GET_QUALITY_CLASS = GET_QUALITY_CLASS;
	GET_QUALITY_DISPLAY = GET_QUALITY_DISPLAY;
	GET_CC_OVERALL_CLASS = GET_CC_OVERALL_CLASS;
	GET_CC_OVERALL_DISPLAY = GET_CC_OVERALL_DISPLAY;
	TOOL_TIP_MESSAGE = TOOL_TIP_MESSAGE;

	constructor(
		public carrierVettingService: CarrierVettingService,
		private restService: RestService,
		private urlService: UrlService,
		private cargochief: Cargochief,
		private sharedService: SharedService,
		public utilService: UtilService
	) {
		super();
	}

	ngOnInit() {
		this.isAccountTypeAdmin = this.utilService.isAccountTypeAdmin();
		this.isMCPEnabled = this.carrierVettingService.isMCPEnabled();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.carrierId || changes.ccScore) {
			if (Number.isInteger(this.ccScore)) {
				this.ccScoreDisplay = this.sharedService.getCarrierScoreToDisplay(
					this.ccScore,
					this.carrierActive,
					this.forBrokerQualityData
				);
			} else {
				this.ccScoreDisplay = 'N/A';
			}
		}
		this.combineAllInfractions();

		// Check if we already have vetting data provided through inputs
		const hasVettingData =
			IS_NOT_NULL_OR_UNDEFINED(this.vettingOverallDisplay) ||
			IS_NOT_NULL_OR_UNDEFINED(this.vettingAuthorityDisplay) ||
			IS_NOT_NULL_OR_UNDEFINED(this.vettingInsuranceDisplay) ||
			IS_NOT_NULL_OR_UNDEFINED(this.vettingOperationsDisplay) ||
			IS_NOT_NULL_OR_UNDEFINED(this.vettingSafetyDisplay) ||
			IS_NOT_NULL_OR_UNDEFINED(this.vettingOtherDisplay);

		// Check if we have any vetting integration enabled
		const hasVettingIntegration =
			this.carrierVettingService.isRMISEnabled() ||
			this.carrierVettingService.isMCPEnabled() ||
			this.carrierVettingService.isHighwayEnabled();

		// Only make API call if we don't have vetting data AND we have an active integration
		if (!hasVettingData && hasVettingIntegration) {
			// Try to get vetting data from API if data is missing and integration is enabled
			this.getCarrierVettingQuality();
		} else {
			// Either we have vetting data already or no integration is enabled
			this.isLoadingCarrierQualityFromMcp = false;
		}
	}

	getCarrierVettingQuality() {
		if (IS_EMPTY(this.mc) && IS_EMPTY(this.dot)) {
			// If no MC/DOT, set loading to false and return early
			this.isLoadingCarrierQualityFromMcp = false;
			return;
		}

		this.isLoadingCarrierQualityFromMcp = true;
		let token = '';
		let url = '';


		if (this.carrierVettingService.isMCPEnabled()) {
			const mcp = this.cargochief.MY_CARRIER_PACKETS;
			token = mcp?.data ? JSON.parse(mcp.data)?.token : '';
			url = this.urlService.getMcpCarrierQuality(
				this.mc,
				this.dot,
				this.isCarrierInNetwork ? this.isCarrierInNetwork : false,
				this.carrierId
			);
		} else if (this.carrierVettingService.isHighwayEnabled()) {
			const highway = this.cargochief.HIGHWAY;
			token = highway?.data ? JSON.parse(highway.data)?.token : '';
			url = this.urlService.getHighwayCarrierQuality(
				this.mc,
				this.dot,
				this.isCarrierInNetwork ? this.isCarrierInNetwork : false,
				this.carrierId
			);
		} else if (this.carrierVettingService.isRMISEnabled()) {
			const rmis = this.cargochief.getIntegrationData('RMIS');
			token = rmis?.data ? JSON.parse(rmis.data)?.token : '';
			url = this.urlService.getRmisCarrierQuality(
				this.mc,
				this.dot,
				this.isCarrierInNetwork ? this.isCarrierInNetwork : false,
				this.carrierId
			);
		} else {
			// Fallback for C4 and other vetting sources - use MCP endpoint
			url = this.urlService.getMcpCarrierQuality(
				this.mc,
				this.dot,
				this.isCarrierInNetwork ? this.isCarrierInNetwork : false,
				this.carrierId
			);
		}

		// Only proceed if we have a valid URL
		if (IS_EMPTY(url)) {
			this.isLoadingCarrierQualityFromMcp = false;
			return;
		}

		this.restService
			.GET(url)
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.isLoadingCarrierQualityFromMcp = false;
				})
			)
			.subscribe(
				(response) => {
					const carrierQuality = response.data;
					this.vettingOverallDisplay = carrierQuality?.overall;
					this.vettingAuthorityDisplay = carrierQuality?.authority;
					this.vettingInsuranceDisplay = carrierQuality?.insurance;
					this.vettingOperationsDisplay = carrierQuality?.operations;
					this.vettingSafetyDisplay = carrierQuality?.safety;
					this.vettingOtherDisplay = carrierQuality?.other;
					this.infractionDetails = carrierQuality?.infractionDetails || {};
					this.combineAllInfractions();
				},
				(error) => {
					// CN-8749: the live carrier-quality (MCP/RMIS/Highway) lookup is best-effort
					// enrichment used only when no stored vetting was supplied. When it fails (e.g.
					// the carrier has no resolvable record), degrade quietly to N/A rather than
					// showing an alarming warning toast — which on a capacity search fires per
					// affected carrier and reads as if something is broken.
					this.isLoadingCarrierQualityFromMcp = false;
					console.warn('Carrier quality lookup failed; showing N/A.', error);
				}
			);
	}

	getOverallDisplayClass() {
		if (['acceptable', 'certified'].includes(this.vettingOverallDisplay?.toLowerCase()?.trim())) {
			return 'Acceptable';
		} else if (
			['unacceptable', 'not certified'].includes(this.vettingOverallDisplay?.toLowerCase()?.trim())
		) {
			return 'Unacceptable';
		}
		return '';
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	tootltipMessage(infractions: string[]): string {
		if (IS_NOT_EMPTY_LIST(infractions)) {
			return infractions.join('\n');
		}
	}

	combineAllInfractions() {
		this.allInfractions = COMBINE_ALL_INFRACTIONS(this.infractionDetails);
	}
}
